import type { DimensionPicture, RoadmapItem, RoadmapView } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { RequestError, api } from '../../lib/api'
import { faNum } from '../platform/labels'

/**
 * «مسیر توسعه» — what the conversation has worked out, as a plan to choose from.
 *
 * Laid out the way Afra's prototype lays it out: the goal and what success
 * looks like, the confirmed root causes, the proposals ranked, and the seven
 * dimensions with where each stands. A proposal is not a task: the
 * organization adds the ones it will act on, and only those reach the plan.
 */
export function RoadmapPage() {
  const roadmap = useQuery({ queryKey: ['roadmap'], queryFn: api.roadmap })

  if (roadmap.isLoading || !roadmap.data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  const { goal, rootCauses, items, dimensions } = roadmap.data
  const live = items.filter((i) => i.status !== 'dismissed')
  const dismissed = items.filter((i) => i.status === 'dismissed')

  return (
    <div className="container stack">
      <div className="card">
        <h1>برنامه توسعه پیشنهادی</h1>
        <p className="meta">
          برنامه از چالش و نتیجه مطلوب شروع می‌شود، اما به همان یک بخش محدود نمی‌ماند. هر پیشنهاد به علتی وصل است
          که خودتان تأییدش کرده‌اید، و فقط پیشنهادهایی که به برنامه اضافه کنید کار حساب می‌شوند.
        </p>
      </div>

      <div className="card">
        <h2>هدف توسعه</h2>
        {goal.challengeFa || goal.outcomeFa ? (
          <dl className="legacy-facts">
            <dt>چالش</dt>
            <dd>{goal.challengeFa ?? 'هنوز مشخص نشده'}</dd>
            <dt>نتیجه مطلوب</dt>
            <dd style={{ fontWeight: 700 }}>{goal.outcomeFa ?? 'هنوز مشخص نشده'}</dd>
          </dl>
        ) : (
          <p className="meta">
            هنوز چالش و نتیجه مطلوب روشن نشده. از <Link to="/journey">گفت‌وگو</Link> شروع کنید.
          </p>
        )}
        {rootCauses.length > 0 && (
          <>
            <h3 style={{ marginTop: 'var(--space-4)' }}>علت ریشه‌ای که تأیید کردید</h3>
            {rootCauses.map((h) => (
              <div key={h.id} className="note">
                <span className="note__dot" />
                <span>{h.textFa}</span>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="card">
        <h2>پیشنهادها</h2>
        {live.length === 0 ? (
          <p className="meta">
            پیشنهادها بعد از اینکه علت ریشه‌ای را در گفت‌وگو تأیید کنید اینجا می‌آیند. تا آن موقع افرا راه‌حل پیشنهاد
            نمی‌کند.
          </p>
        ) : (
          live.map((item) => <ItemRow key={item.id} item={item} />)
        )}
        {dismissed.length > 0 && <Dismissed items={dismissed} />}
      </div>

      <div className="card">
        <h2>تصویر توسعه در همه بخش‌ها</h2>
        <p className="meta">
          چالش اصلی مسیر را شروع کرده، اما مرور همه بخش‌ها کمک می‌کند موضوع‌های مرتبط هم دیده شوند. «موضوع قابل بررسی»
          هنوز اقدام نیست؛ اگر مهم باشد، جداگانه بررسی می‌شود.
        </p>
        <DimensionTable dimensions={dimensions} />
      </div>
    </div>
  )
}

const RELATION_FA: Record<NonNullable<DimensionPicture['relation']>, { fa: string; badge: string }> = {
  deep_dive: { fa: 'بررسی عمیق', badge: 'badge--bad' },
  related: { fa: 'مرتبط', badge: 'badge--warn' },
  reviewed: { fa: 'مرور شده', badge: 'badge--stored' },
}

function DimensionTable({ dimensions }: { dimensions: DimensionPicture[] }) {
  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>بخش</th>
            <th>وضعیت در این مسیر</th>
            <th>نتیجه مرور</th>
            <th>آنچه می‌دانیم</th>
          </tr>
        </thead>
        <tbody>
          {dimensions.map((d) => (
            <tr key={d.code}>
              <td>{d.titleFa}</td>
              <td>
                {d.relation ? (
                  <span className={`badge ${RELATION_FA[d.relation].badge}`}>{RELATION_FA[d.relation].fa}</span>
                ) : (
                  <span className="muted">هنوز مرور نشده</span>
                )}
              </td>
              <td>{d.findingFa ?? <span className="muted">—</span>}</td>
              <td>
                <span className="num">
                  {faNum(d.known)}/{faNum(d.total)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ItemRow({ item }: { item: RoadmapItem }) {
  const queryClient = useQueryClient()
  const [planning, setPlanning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const done = (view: RoadmapView) => {
    setError(null)
    setPlanning(false)
    queryClient.setQueryData(['roadmap'], view)
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['chat-context'] })
  }
  const fail = (err: unknown) => setError(err instanceof RequestError ? err.messageFa : 'انجام نشد.')

  const dismiss = useMutation({
    mutationFn: () => api.dismissRoadmapItem(item.id, true),
    onSuccess: done,
    onError: fail,
  })

  return (
    <article className={`road-item road-item--p${item.priority}`}>
      <div className="meta">
        {item.kind === 'intervention' ? 'مداخله پیشنهادی' : `اولویت ${faNum(item.priority)}`} · {item.dimensionTitleFa}
      </div>
      <div style={{ fontWeight: 700 }}>{item.actionFa}</div>
      {item.hypothesisFa && <div className="task__done">علت ریشه‌ای تأییدشده: {item.hypothesisFa}</div>}
      {item.rationaleFa && <div className="task__done">شاهد: {item.rationaleFa}</div>}
      {item.outcomeFa && <div className="task__done">نتیجه: {item.outcomeFa}</div>}
      <div className="task__done">انجام شده یعنی: {item.doneLooksLikeFa}</div>

      {error && <p className="notice notice--error">{error}</p>}

      {item.status === 'planned' ? (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <span className="badge badge--good">در برنامه است</span>{' '}
          <Link to="/planning" className="btn btn--quiet btn--sm">
            دیدن در برنامه‌ریزی
          </Link>
        </div>
      ) : planning ? (
        <PlanForm item={item} onDone={done} onError={fail} onCancel={() => setPlanning(false)} />
      ) : (
        <div className="row kanban__actions">
          <button type="button" className="btn btn--sm" onClick={() => setPlanning(true)}>
            افزودن به برنامه
          </button>
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate()}
          >
            فعلاً نه
          </button>
        </div>
      )}
    </article>
  )
}

function PlanForm({
  item,
  onDone,
  onError,
  onCancel,
}: {
  item: RoadmapItem
  onDone: (view: RoadmapView) => void
  onError: (err: unknown) => void
  onCancel: () => void
}) {
  const [owner, setOwner] = useState('')
  const [days, setDays] = useState('30')
  const [doneFa, setDoneFa] = useState(item.doneLooksLikeFa)
  const plan = useMutation({
    mutationFn: () =>
      api.planRoadmapItem(item.id, {
        ownerFa: owner.trim() || null,
        dueInDays: days.trim() === '' ? null : Number(days),
        doneLooksLikeFa: doneFa.trim() !== item.doneLooksLikeFa ? doneFa.trim() : undefined,
      }),
    onSuccess: onDone,
    onError,
  })
  const dayCount = Number(days)
  const valid = days.trim() === '' || (Number.isInteger(dayCount) && dayCount >= 0 && dayCount <= 730)

  return (
    <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
      <div className="grid-2">
        <div className="field">
          <label htmlFor={`plan-owner-${item.id}`}>چه کسی انجامش می‌دهد</label>
          <input id={`plan-owner-${item.id}`} value={owner} onChange={(e) => setOwner(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`plan-days-${item.id}`}>مهلت: چند روز دیگر</label>
          <input
            id={`plan-days-${item.id}`}
            className="num"
            inputMode="numeric"
            value={days}
            onChange={(e) => setDays(e.target.value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))))}
            placeholder="خالی یعنی بدون مهلت"
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor={`plan-done-${item.id}`}>انجام شده یعنی</label>
        <textarea id={`plan-done-${item.id}`} value={doneFa} onChange={(e) => setDoneFa(e.target.value)} />
      </div>
      <div className="row">
        <button type="button" className="btn btn--sm" disabled={plan.isPending || !valid} onClick={() => plan.mutate()}>
          {plan.isPending ? 'در حال افزودن…' : 'به برنامه اضافه کن'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel}>
          بی‌خیال
        </button>
      </div>
    </div>
  )
}

function Dismissed({ items }: { items: RoadmapItem[] }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const restore = useMutation({
    mutationFn: (id: number) => api.dismissRoadmapItem(id, false),
    onSuccess: (view) => queryClient.setQueryData(['roadmap'], view),
  })
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <button type="button" className="btn btn--quiet btn--sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        پیشنهادهای کنار گذاشته <span className="num">({faNum(items.length)})</span>
      </button>
      {open &&
        items.map((item) => (
          <div key={item.id} className="note">
            <span className="note__dot" />
            <span>
              {item.actionFa}{' '}
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                disabled={restore.isPending}
                onClick={() => restore.mutate(item.id)}
              >
                برگردان
              </button>
            </span>
          </div>
        ))}
    </div>
  )
}
